import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { channelLabel, type Channel } from '../channels';
import { orgPath } from '../nav';

type AuditChannel = Channel | 'health_check';

interface AccessEvent {
  id: string;
  issuedAt: string;
  expiresAt: string;
  revokedAt: string | null;
  channel: AuditChannel;
  agent: string | null;
  agentScope: string | null;
  ticketRef: string | null;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  mfaVerifiedAt: string | null;
  instanceUrl: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  automationId: string | null;
  automationName: string | null;
  customerOrgId: string;
  orgLabel: string;
  orgEnvironment: string;
  identityId: string;
  identityUsername: string;
  identityKind: 'shared' | 'personal';
  grantedByEmail: string | null;
}

interface Retention {
  months: number | null;
  /** The newest cutoff retention has already applied. Everything before it has been deleted. */
  purgedThrough: string | null;
}

interface Page {
  events: AccessEvent[];
  scope: 'self' | 'tenant';
  retention: Retention;
  nextBefore: string | null;
  nextBeforeId: string | null;
}

interface OrgSummary { id: string; label: string }

const CHANNEL_CHOICES: AuditChannel[] = ['browser', 'cli', 'agent', 'automation', 'health_check'];

const label = (c: AuditChannel): string => (c === 'health_check' ? 'Health check' : channelLabel(c));

/** Who the session was minted for. A health check has no principal at all: Kruvi Access asked, on its own behalf. */
function who(e: AccessEvent): { name: string; detail: string | null } {
  if (e.automationName) return { name: e.automationName, detail: 'automation' };
  if (e.userEmail) return { name: e.userName || e.userEmail, detail: e.userName ? e.userEmail : null };
  return { name: 'Kruvi Access', detail: 'health check' };
}

/** Revoked beats expired: the moment it was cut short is the part worth seeing. */
function state(e: AccessEvent, now: number): { text: string; className: string } {
  if (e.revokedAt) return { text: 'revoked', className: 'pill off' };
  if (new Date(e.expiresAt).getTime() <= now) return { text: 'expired', className: 'pill' };
  return { text: 'live', className: 'pill on' };
}

/**
 * The session log. Every session Kruvi Access has minted for this company: who, into which org, as which
 * Salesforce identity, why, and whether it is still open. Owners and admins see the whole company's
 * history; a member sees their own, which is what the server hands back either way.
 *
 * Mounted twice: as the workspace's Session log, where the org is a filter like any other, and as an
 * org's own Session log, where the org is the address. In the second the org is fixed and its selector
 * is not offered, since choosing a different org from inside one would leave the page under the wrong
 * heading. The layout around it names the page in both, so it carries no heading of its own.
 */
export function Audit() {
  const { tenantId = '', orgId } = useParams();
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState<AccessEvent[]>([]);
  const [scope, setScope] = useState<'self' | 'tenant'>('tenant');
  const [retention, setRetention] = useState<Retention>({ months: null, purgedThrough: null });
  const [next, setNext] = useState<{ before: string; beforeId: string } | null>(null);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [searchBox, setSearchBox] = useState(params.get('q') ?? '');
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The filters live in the URL, so one view of the log can be handed to a colleague as a link.
  const search = params.get('q') ?? '';
  const channel = params.get('channel') ?? '';
  // An org's own page wins over the query string. The ?org= form is the older link into the workspace
  // log, which still works there.
  const org = orgId ?? params.get('org') ?? '';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  /** The filters, as the API takes them. Shared by the table and the export, so the file matches the page. */
  function filterParams(): URLSearchParams {
    const query = new URLSearchParams();
    if (search) query.set('q', search);
    if (channel) query.set('channel', channel);
    if (org) query.set('org', org);
    if (from) query.set('from', from);
    if (to) query.set('to', to);
    return query;
  }

  function setFilter(key: string, value: string) {
    const nextParams = new URLSearchParams(params);
    if (value) nextParams.set(key, value);
    else nextParams.delete(key);
    setParams(nextParams, { replace: true });
  }

  const load = useCallback(async (cursor?: { before: string; beforeId: string } | null) => {
    setBusy(true);
    setError(null);
    try {
      const query = filterParams();
      if (cursor) {
        query.set('before', cursor.before);
        query.set('beforeId', cursor.beforeId);
      }
      const page = await api<Page>(`/api/tenants/${tenantId}/audit?${query}`);
      setEvents((prev) => (cursor ? [...prev, ...page.events] : page.events));
      setScope(page.scope);
      setRetention(page.retention);
      setNext(page.nextBefore && page.nextBeforeId ? { before: page.nextBefore, beforeId: page.nextBeforeId } : null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the session log');
    } finally {
      setBusy(false);
    }
    // filterParams() reads exactly these and nothing else, which is why it is safe to leave out.
  }, [tenantId, search, channel, org, from, to]);

  useEffect(() => { void load(); }, [load]);

  // Only the workspace log offers a choice of org, so only it needs the list.
  useEffect(() => {
    if (orgId) return;
    api<{ orgs: OrgSummary[] }>(`/api/tenants/${tenantId}/orgs`)
      .then((o) => setOrgs(o.orgs))
      .catch(() => undefined);
  }, [tenantId, orgId]);

  const now = Date.now();

  return (
    <>
      {error && <div className="alert">{error}</div>}

      <div className="card">
        <form className="row" onSubmit={(e) => { e.preventDefault(); setFilter('q', searchBox); }}>
          <input
            aria-label="Search"
            placeholder={orgId ? 'Search person, identity, ticket or reason' : 'Search person, org, identity, ticket or reason'}
            value={searchBox}
            onChange={(e) => setSearchBox(e.target.value)}
          />
          <select aria-label="Way in" value={channel} onChange={(e) => setFilter('channel', e.target.value)}>
            <option value="">Every way in</option>
            {CHANNEL_CHOICES.map((c) => <option key={c} value={c}>{label(c)}</option>)}
          </select>
          {!orgId && (
            <select aria-label="Org" value={org} onChange={(e) => setFilter('org', e.target.value)}>
              <option value="">Every customer org</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          )}
          <button type="submit" disabled={busy}>Search</button>
        </form>
        <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
          <label className="small">
            From
            <input type="date" value={from} onChange={(e) => setFilter('from', e.target.value)} />
          </label>
          <label className="small">
            To
            <input type="date" value={to} onChange={(e) => setFilter('to', e.target.value)} />
          </label>
          <span className="spacer" />
          {/*
            A plain link, not a fetch: the browser saves the file itself, and the same cookie that reads
            the page authorises the download. The server applies the same narrowing either way, so a
            member's export is their own events just as the table is, and an org's page exports that org.
          */}
          <a className="button secondary" href={`/api/tenants/${tenantId}/audit.csv?${filterParams()}`}>
            Export CSV
          </a>
        </div>
      </div>

      <div className="card flush">
        <table>
          <thead><tr><th>When</th><th>Who</th><th>Into</th><th>How</th><th>Why</th><th>State</th><th></th></tr></thead>
          <tbody>
            {events.length === 0 && !busy && (
              <tr><td colSpan={7} className="empty">Nothing recorded yet.</td></tr>
            )}
            {events.map((e) => {
              const actor = who(e);
              const status = state(e, now);
              return (
                <Fragment key={e.id}>
                  <tr>
                    <td className="small">{new Date(e.issuedAt).toLocaleString()}</td>
                    <td>{actor.name}{actor.detail && <><br /><span className="small muted">{actor.detail}</span></>}</td>
                    <td>
                      <Link to={orgPath(tenantId, e.customerOrgId)}>{e.orgLabel}</Link>
                      <br /><span className="small muted mono">{e.identityUsername}</span>
                    </td>
                    <td className="small">
                      <span className="pill">{label(e.channel)}</span>
                      {e.agent && <><br /><span className="muted">{e.agent}</span></>}
                    </td>
                    <td className="small">
                      {e.ticketRef && <span className="mono">{e.ticketRef}</span>}
                      {e.ticketRef && e.reason && <br />}
                      {e.reason}
                      {!e.ticketRef && !e.reason && <span className="muted">not asked for</span>}
                    </td>
                    <td><span className={status.className}>{status.text}</span></td>
                    <td className="actions-cell">
                      <button type="button" className="secondary" onClick={() => setOpen(open === e.id ? null : e.id)}>
                        {open === e.id ? 'Hide' : 'Detail'}
                      </button>
                    </td>
                  </tr>
                  {open === e.id && (
                    <tr>
                      <td colSpan={7}>
                        <div className="small">
                          <p>
                            Issued {new Date(e.issuedAt).toLocaleString()}, expires {new Date(e.expiresAt).toLocaleString()}
                            {e.revokedAt ? `, revoked ${new Date(e.revokedAt).toLocaleString()}` : ''}.
                          </p>
                          <p>
                            As <span className="mono">{e.identityUsername}</span> ({e.identityKind}) in {e.orgLabel},
                            a {e.orgEnvironment} org at <span className="mono">{e.instanceUrl}</span>.
                          </p>
                          {e.agent && <p>Agent: {e.agent}{e.agentScope ? ` (${e.agentScope})` : ''}.</p>}
                          <p>
                            {e.grantedByEmail ? `Granted by ${e.grantedByEmail}.` : 'No grant recorded against this event.'}{' '}
                            {e.mfaVerifiedAt
                              ? `Second factor verified ${new Date(e.mfaVerifiedAt).toLocaleString()}.`
                              : 'No second factor on this session.'}
                          </p>
                          <p className="muted">
                            From {e.ip ? <span className="mono">{e.ip}</span> : 'an address that was not recorded'}
                            {e.userAgent ? ` · ${e.userAgent}` : ''}
                          </p>
                          <p className="muted">Event id <span className="mono">{e.id}</span></p>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {next && (
        <button type="button" className="secondary block" style={{ marginTop: 16 }} onClick={() => void load(next)} disabled={busy}>
          Load older
        </button>
      )}
      <p className="small muted" style={{ marginTop: 16 }}>
        {/* The org's page heading says "every session", which for a member is not what the server sent. */}
        {scope === 'self' ? "These are your own sessions; an owner or admin sees everyone's. " : ''}
        Nothing here is ever edited, and a session that was cut short keeps its row: the log is append-only
        in the database itself.{' '}
        {retention.months
          ? `Events are kept for ${retention.months} months and then deleted, which is the only way a row ever leaves.`
          : ''}{' '}
        {retention.purgedThrough
          ? `Everything issued before ${new Date(retention.purgedThrough).toLocaleDateString()} has already been removed by that policy.`
          : ''}
      </p>
    </>
  );
}
